import React from 'react';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const MaritimeUseCase = () => {
  const keyScenarios = [
    "Vessel-to-shore coordination with resilience to network disruptions",
    "Predictive maintenance orchestration across sensor networks and legacy systems",
    "Dynamic cargo routing with real-time optimization and operator approval"
  ];

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
      <div className="max-w-7xl mx-auto">
        <div 
          className="rounded-xl p-8 md:p-12"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Left Column - Text Content */}
            <div className="flex flex-col justify-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
                style={{ backgroundColor: 'rgba(79, 179, 184, 0.15)', border: '1px solid rgba(79, 179, 184, 0.4)', color: '#4FB3B8' }}
              >
                Maritime
              </div>

              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#E8EDDF' }}>
                Maritime
              </h2>

              <p className="text-base leading-relaxed mb-8" style={{ color: '#9CA3AF' }}>
                Fleet management, edge data processing, and predictive maintenance across distributed vessel networks. Secure edge-to-cloud operations with intermittent connectivity, coordinating onboard systems, shore operations, and maintenance teams.
              </p>

              <div className="mb-8">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#9CA3AF' }}>
                  Key Scenarios
                </h3>
                <ul className="space-y-4">
                  {keyScenarios.map((scenario, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div 
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ backgroundColor: '#4FB3B8' }}
                      ></div>
                      <span className="text-base" style={{ color: '#E8EDDF' }}>{scenario}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link 
                to="/use-cases/maritime"
                className="inline-flex items-center gap-2 text-base font-medium transition-colors duration-250 w-fit"
                style={{ color: '#4FB3B8' }}
              >
                View Maritime Use Case
                <Icon name="ArrowRight" size={16} style={{ color: '#4FB3B8' }} />
              </Link>
            </div>

            {/* Right Column - Diagram */}
            <div className="flex items-center justify-center">
              <div 
                className="w-full rounded-lg p-6 relative overflow-hidden"
                style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
              >
                {/* Grid Pattern Background */}
                <div 
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: `
                      linear-gradient(rgba(79, 179, 184, 0.3) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(79, 179, 184, 0.3) 1px, transparent 1px)
                    `,
                    backgroundSize: '30px 30px'
                  }}
                ></div>
                
                <svg width="100%" height="380" viewBox="0 0 480 380" className="max-w-full relative z-10">
                  
                  {/* Top Row - Atlantic, Pacific, Mediterranean */}
                  <rect x="60" y="30" width="100" height="55" rx="6" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="110" y="62" textAnchor="middle" fill="#E8EDDF" fontSize="11" fontWeight="600">ATLANTIC</text>
                  
                  <rect x="190" y="30" width="100" height="55" rx="6" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="240" y="62" textAnchor="middle" fill="#E8EDDF" fontSize="11" fontWeight="600">PACIFIC</text>
                  
                  <rect x="320" y="30" width="120" height="55" rx="6" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="380" y="62" textAnchor="middle" fill="#E8EDDF" fontSize="11" fontWeight="600">MEDITERRANEAN</text>
                  
                  {/* Connection lines from top row */}
                  <line x1="110" y1="85" x2="110" y2="120" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="240" y1="85" x2="240" y2="120" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="380" y1="85" x2="380" y2="120" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Horizontal line connecting vertical lines */}
                  <line x1="110" y1="120" x2="380" y2="120" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Center vertical line to Shore Operations */}
                  <line x1="240" y1="120" x2="240" y2="150" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Shore Operations Box */}
                  <rect x="90" y="150" width="300" height="75" rx="6" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="240" y="182" textAnchor="middle" fill="#E8EDDF" fontSize="14" fontWeight="700">SHORE OPERATIONS</text>
                  <text x="240" y="205" textAnchor="middle" fill="#9CA3AF" fontSize="10">Fleet Coordination • Maintenance • Logistics</text>
                  
                  {/* Connection lines from Shore Operations to bottom */}
                  <line x1="240" y1="225" x2="240" y2="260" stroke="#9CA3AF" strokeWidth="2" />
                  <line x1="140" y1="260" x2="340" y2="260" stroke="#9CA3AF" strokeWidth="2" />
                  
                  {/* Vertical lines to bottom circles */}
                  <line x1="140" y1="260" x2="140" y2="290" stroke="#9CA3AF" strokeWidth="2" />
                  <line x1="240" y1="260" x2="240" y2="290" stroke="#9CA3AF" strokeWidth="2" />
                  <line x1="340" y1="260" x2="340" y2="290" stroke="#9CA3AF" strokeWidth="2" />
                  
                  {/* Bottom Row - MAINT, OPS, LOG circles */}
                  <circle cx="140" cy="325" r="35" fill="#242423" stroke="#9CA3AF" strokeWidth="2" />
                  <text x="140" y="330" textAnchor="middle" fill="#E8EDDF" fontSize="12" fontWeight="600">MAINT</text>
                  
                  <circle cx="240" cy="325" r="35" fill="#242423" stroke="#9CA3AF" strokeWidth="2" />
                  <text x="240" y="330" textAnchor="middle" fill="#E8EDDF" fontSize="12" fontWeight="600">OPS</text>
                  
                  <circle cx="340" cy="325" r="35" fill="#242423" stroke="#9CA3AF" strokeWidth="2" />
                  <text x="340" y="330" textAnchor="middle" fill="#E8EDDF" fontSize="12" fontWeight="600">LOG</text>
                  
                </svg>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default MaritimeUseCase;